import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  Chip,
  TextField,
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  LocalAtm as LocalAtmIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import api from '../../services/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, ChartTooltip, Legend);

const formatAmount = (amount) => {
  if (!amount && amount !== 0) return '-';
  return amount >= 10000 ? `${(amount / 10000).toFixed(0)}万` : amount;
};

const formatYuan = (yuan) => {
  if (!yuan && yuan !== 0) return '-';
  return `¥${Number(yuan).toFixed(2)}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const SOURCE_CONFIG = {
  wechat: { label: '微信支付', color: '#07c160', bg: 'rgba(7,193,96,0.15)', icon: 'W' },
  alipay: { label: '支付宝', color: '#1677ff', bg: 'rgba(22,119,255,0.15)', icon: '支' },
  cardKey: { label: '卡密充值', color: '#76ff03', bg: 'rgba(118,255,3,0.15)', icon: 'K' }
};

const RechargeRecordPanel = ({ timeRange = 30, onTimeRangeChange }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');

  const tabRef = useRef(0);
  const searchRef = useRef('');

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const data = await api.getAdminRechargeStats({ days: timeRange });
      if (data?.success) setStats(data.data);
    } catch (e) {
      console.error('[充值面板] 获取统计失败:', e);
      setStats({});
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchRecords = async (page, targetTab, targetSearch) => {
    setLoading(true);
    try {
      // Tab 索引对应 source：0=全部, 1=微信, 2=支付宝, 3=卡密
      const sourceMap = { 1: 'wechat', 2: 'alipay', 3: 'cardKey' };
      const sourceParam = sourceMap[targetTab] || 'all';

      const data = await api.getAdminAllRechargeRecords({
        page,
        limit: 20,
        source: sourceParam || 'all',
        search: (targetSearch || '').trim() || undefined
      });

      if (data?.success) {
        setRecords(data.data.list || []);
        if (data.data.pagination) {
          setPagination({
            page: data.data.pagination.page,
            total: data.data.pagination.total,
            pages: data.data.pagination.pages
          });
        }
      }
    } catch (e) {
      console.error('[充值面板] 获取记录失败:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Tab 切换时：重置搜索、获取对应类型数据
  useEffect(() => {
    tabRef.current = currentTab;
    setSearchText('');
    setSearchInput('');
    searchRef.current = '';
    setPagination(prev => ({ ...prev, page: 1 }));
    if (currentTab >= 0 && currentTab <= 3) {
      fetchRecords(1, currentTab, '');
    }
  }, [currentTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue);
    tabRef.current = newValue;
    setPagination(prev => ({ ...prev, page: 1 }));
    setSearchText('');
    setSearchInput('');
    searchRef.current = '';
  };

  const handleSearch = () => {
    const val = searchInput;
    setSearchText(val);
    searchRef.current = val;
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleChangePage = (_, newPage) => {
    const targetTab = tabRef.current;
    const targetSearch = searchRef.current;
    setPagination(prev => ({ ...prev, page: newPage + 1 }));
    fetchRecords(newPage + 1, targetTab, targetSearch);
  };

  const handleRefresh = () => {
    const targetTab = tabRef.current;
    const targetSearch = searchRef.current;
    fetchStats();
    if (targetTab >= 0 && targetTab <= 3) {
      fetchRecords(pagination.page, targetTab, targetSearch);
    }
  };

  // 统计卡片 - 使用更安全的空值判断
  const StatCard = ({ label, value, sub, color }) => {
    // 安全显示值：只处理 null 和 undefined，空字符串、0 都是有效值
    const displayValue = (value === null || value === undefined) ? '-' : value;
    return (
      <Box sx={{
        p: 2,
        borderRadius: 2,
        background: 'rgba(6, 21, 44, 0.6)',
        border: `1px solid ${color}30`,
        textAlign: 'center',
        minWidth: 0
      }}>
        <Typography sx={{ fontSize: '0.7rem', color: 'rgba(214,227,252,0.6)', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color, lineHeight: 1.2 }}>
          {statsLoading ? '-' : displayValue}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.65rem', color: 'rgba(214,227,252,0.45)', mt: 0.5 }}>
            {sub}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Card sx={{
      borderRadius: 3,
      background: 'rgba(6, 21, 44, 0.72)',
      border: '1px solid rgba(0, 229, 255, 0.14)',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
      backdropFilter: 'blur(18px)'
    }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2} spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              background: 'linear-gradient(135deg, #4dd0e1 0%, #00838f 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0
            }}>
              <LocalAtmIcon sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#e4f1ff', lineHeight: 1.2, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                充值记录面板
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(214,227,252,0.55)', display: { xs: 'none', sm: 'block' } }}>
                微信 / 支付宝 / 卡密 · 分类统计
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <ButtonGroup variant="outlined" size="small" sx={{
              '& .MuiButton-root': {
                color: '#00e5ff', borderColor: 'rgba(0,229,255,0.35)',
                fontSize: '0.72rem', py: 0.3, minWidth: 40,
                '&.Mui-selected': { backgroundColor: 'rgba(0,229,255,0.18)', color: '#fff' }
              }
            }}>
              {[7, 14, 30].map(v => (
                <Button key={v} onClick={() => onTimeRangeChange?.(v)} variant={timeRange === v ? 'contained' : 'outlined'}>
                  {v}天
                </Button>
              ))}
            </ButtonGroup>
            <Tooltip title="刷新数据">
              <IconButton onClick={handleRefresh} disabled={loading}
                sx={{ color: '#00e5ff', border: '1px solid rgba(0,229,255,0.2)' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* 统计卡片 */}
        {statsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} sx={{ color: '#00e5ff' }} />
          </Box>
        ) : (
          <>
            {/* 第一行：今日充值 */}
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(214,227,252,0.5)', mb: 1, fontWeight: 600, letterSpacing: '0.5px' }}>
                今日充值
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={4} sm={2}>
                  <StatCard
                    label="微信笔数"
                    value={stats.today?.wechatCount || 0}
                    sub={`收款 ${formatYuan(stats.today?.wechatRevenue)}`}
                    color="#07c160"
                  />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <StatCard
                    label="支付宝笔数"
                    value={stats.today?.alipayCount || 0}
                    sub={`收款 ${formatYuan(stats.today?.alipayRevenue)}`}
                    color="#1677ff"
                  />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <StatCard
                    label="卡密笔数"
                    value={stats.today?.cardKeyCount || 0}
                    color="#76ff03"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatCard
                    label="今日总收款"
                    value={formatYuan((stats.today?.wechatRevenue || 0) + (stats.today?.alipayRevenue || 0))}
                    sub={`共 ${stats.today?.totalCount || 0} 笔`}
                    color="#ffd600"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatCard
                    label={`近${timeRange}天总充值字数`}
                    value={formatAmount((stats.wechat?.totalAmount || 0) + (stats.alipay?.totalAmount || 0) + (stats.cardKey?.totalAmount || 0))}
                    sub={`近 ${timeRange} 天`}
                    color="#ba68c8"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* 第二行：三类充值明细 */}
            <Grid container spacing={1.5} mb={2}>
              <Grid item xs={4}>
                <Box sx={{ p: 1.5, borderRadius: 2, background: 'rgba(7,193,96,0.08)', border: '1px solid rgba(7,193,96,0.2)', textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#07c160', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#fff', fontWeight: 800 }}>W</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#07c160', fontWeight: 700 }}>微信支付</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#07c160' }}>
                    {formatYuan(stats.wechat?.totalRevenue || 0)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: 'rgba(7,193,96,0.6)' }}>
                    {stats.wechat?.count || 0} 笔 · {formatAmount(stats.wechat?.totalAmount || 0)}字
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ p: 1.5, borderRadius: 2, background: 'rgba(22,119,255,0.08)', border: '1px solid rgba(22,119,255,0.2)', textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#1677ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#fff', fontWeight: 800 }}>支</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#1677ff', fontWeight: 700 }}>支付宝</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#1677ff' }}>
                    {formatYuan(stats.alipay?.totalRevenue || 0)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: 'rgba(22,119,255,0.6)' }}>
                    {stats.alipay?.count || 0} 笔 · {formatAmount(stats.alipay?.totalAmount || 0)}字
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ p: 1.5, borderRadius: 2, background: 'rgba(118,255,3,0.08)', border: '1px solid rgba(118,255,3,0.2)', textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#76ff03', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#1a1a1a', fontWeight: 800 }}>K</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#76ff03', fontWeight: 700 }}>卡密充值</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#76ff03' }}>
                    {formatAmount(stats.cardKey?.totalAmount || 0)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: 'rgba(118,255,3,0.6)' }}>
                    {stats.cardKey?.count || 0} 笔（无收款金额）
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Tabs */}
            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              value={currentTab}
              onChange={handleTabChange}
              sx={{
                mb: 2,
                minHeight: 36,
                '& .MuiTabs-indicator': { backgroundColor: '#00e5ff', height: 2 },
                '& .MuiTabs-scrollButtons': { color: 'rgba(0,229,255,0.5)' },
                '& .MuiTab-root': {
                  minHeight: 36,
                  minWidth: 'auto',
                  color: 'rgba(214,227,252,0.55)',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1.5,
                  transition: 'all 0.2s',
                  '&.Mui-selected': { color: '#00e5ff', background: 'rgba(0,229,255,0.12)' }
                }
              }}
            >
              <Tab label="全部" value={0} />
              <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#07c160', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 800 }}>W</Typography>
                </Box>
                微信
              </Box>} value={1} />
              <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#1677ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 800 }}>支</Typography>
                </Box>
                支付宝
              </Box>} value={2} />
              <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#76ff03', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#1a1a1a', fontWeight: 800 }}>K</Typography>
                </Box>
                卡密
              </Box>} value={3} />
              <Tab label="日统计" value={4} icon={<BarChartIcon sx={{ fontSize: 14 }} />} iconPosition="start" />
              <Tab label="月统计" value={5} icon={<BarChartIcon sx={{ fontSize: 14 }} />} iconPosition="start" />
            </Tabs>

            {/* 日统计图表 */}
            {currentTab === 4 && stats.dailyStats && stats.dailyStats.length > 0 && (
              <Box sx={{ mb: 2, p: 2, borderRadius: 2, background: 'rgba(6, 21, 44, 0.6)', border: '1px solid rgba(0,229,255,0.1)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#07c160' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: '#07c160' }}>微信收款</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1677ff' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: '#1677ff' }}>支付宝收款</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: '#ffd600' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: '#ffd600' }}>合计收款</Typography>
                  </Box>
                </Box>
                <Box height={200}>
                  <Bar
                    data={{
                      labels: stats.dailyStats.map(d => d.date),
                      datasets: [
                        {
                          label: '微信收款',
                          data: stats.dailyStats.map(d => d.wechatRevenue),
                          backgroundColor: 'rgba(7, 193, 96, 0.7)',
                          borderColor: '#07c160',
                          borderWidth: 1
                        },
                        {
                          label: '支付宝收款',
                          data: stats.dailyStats.map(d => d.alipayRevenue),
                          backgroundColor: 'rgba(22, 119, 255, 0.7)',
                          borderColor: '#1677ff',
                          borderWidth: 1
                        },
                        {
                          label: '合计收款',
                          data: stats.dailyStats.map(d => d.totalRevenue),
                          backgroundColor: 'rgba(255, 214, 0, 0.5)',
                          borderColor: '#ffd600',
                          borderWidth: 1
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: ctx => `${ctx.dataset.label}: ¥${ctx.raw?.toFixed(2) || 0}`
                          }
                        }
                      },
                      scales: {
                        x: { ticks: { color: 'rgba(214,227,252,0.5)', font: { size: 10 } }, grid: { color: 'rgba(0,229,255,0.06)' } },
                        y: { ticks: { color: '#ffd600', font: { size: 10 }, callback: v => `¥${v}` }, grid: { color: 'rgba(0,229,255,0.06)' } }
                      }
                    }}
                  />
                </Box>
              </Box>
            )}

            {currentTab === 4 && (!stats.dailyStats || stats.dailyStats.length === 0) && (
              <Box sx={{ textAlign: 'center', py: 4, color: 'rgba(214,227,252,0.4)' }}>
                <Typography variant="body2">暂无日统计数据</Typography>
              </Box>
            )}

            {/* 月统计图表 */}
            {currentTab === 5 && stats.monthlyStats && stats.monthlyStats.length > 0 && (
              <Box sx={{ mb: 2, p: 2, borderRadius: 2, background: 'rgba(6, 21, 44, 0.6)', border: '1px solid rgba(0,229,255,0.1)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#07c160' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: '#07c160' }}>微信收款</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1677ff' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: '#1677ff' }}>支付宝收款</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: '#ffd600' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: '#ffd600' }}>合计收款</Typography>
                  </Box>
                </Box>
                <Box height={200}>
                  <Bar
                    data={{
                      labels: stats.monthlyStats.map(m => m.month),
                      datasets: [
                        {
                          label: '微信收款',
                          data: stats.monthlyStats.map(m => m.wechatRevenue),
                          backgroundColor: 'rgba(7, 193, 96, 0.7)',
                          borderColor: '#07c160',
                          borderWidth: 1
                        },
                        {
                          label: '支付宝收款',
                          data: stats.monthlyStats.map(m => m.alipayRevenue),
                          backgroundColor: 'rgba(22, 119, 255, 0.7)',
                          borderColor: '#1677ff',
                          borderWidth: 1
                        },
                        {
                          label: '合计收款',
                          data: stats.monthlyStats.map(m => m.totalRevenue),
                          backgroundColor: 'rgba(255, 214, 0, 0.5)',
                          borderColor: '#ffd600',
                          borderWidth: 1
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: ctx => `${ctx.dataset.label}: ¥${ctx.raw?.toFixed(2) || 0}`
                          }
                        }
                      },
                      scales: {
                        x: { ticks: { color: 'rgba(214,227,252,0.5)', font: { size: 10 } }, grid: { color: 'rgba(0,229,255,0.06)' } },
                        y: { ticks: { color: '#ffd600', font: { size: 10 }, callback: v => `¥${v}` }, grid: { color: 'rgba(0,229,255,0.06)' } }
                      }
                    }}
                  />
                </Box>
              </Box>
            )}

            {currentTab === 5 && (!stats.monthlyStats || stats.monthlyStats.length === 0) && (
              <Box sx={{ textAlign: 'center', py: 4, color: 'rgba(214,227,252,0.4)' }}>
                <Typography variant="body2">暂无月统计数据</Typography>
              </Box>
            )}

            {/* 表格（Tab 0-3） */}
            {currentTab >= 0 && currentTab <= 3 && (
              <>
                {/* 搜索栏 */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch" mb={2}>
                  <TextField
                    size="small"
                    placeholder="搜索用户名、套餐名、卡号..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'rgba(214,227,252,0.4)', fontSize: 18 }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(6, 21, 44, 0.6)',
                        '& fieldset': { borderColor: 'rgba(0,229,255,0.18)' },
                        '&:hover fieldset': { borderColor: 'rgba(0,229,255,0.35)' },
                        '&.Mui-focused fieldset': { borderColor: '#00e5ff' },
                        '& input': { color: '#e4f1ff', fontSize: '0.82rem', py: 0.75 }
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleSearch}
                    size="small"
                    sx={{
                      color: '#00e5ff', borderColor: 'rgba(0,229,255,0.3)',
                      '&:hover': { borderColor: '#00e5ff', background: 'rgba(0,229,255,0.1)' },
                      borderRadius: 2, px: 2, height: 36, flexShrink: 0
                    }}
                  >
                    搜索
                  </Button>
                </Stack>

                {/* 表格 */}
                <TableContainer
                  component={Paper}
                  sx={{
                    backgroundColor: 'transparent',
                    borderRadius: 2,
                    maxHeight: 480,
                    overflowX: 'auto',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: 6, height: 6 },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,229,255,0.2)', borderRadius: 3 },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                    '& table': { minWidth: 560 }
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow sx={{
                        '& th': {
                          backgroundColor: 'rgba(6, 21, 44, 0.95)',
                          color: 'rgba(214,227,252,0.8)',
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          py: 1,
                          px: 1,
                          borderBottom: '1px solid rgba(0,229,255,0.15)',
                          whiteSpace: 'nowrap'
                        }
                      }}>
                        <TableCell>来源</TableCell>
                        <TableCell>用户名</TableCell>
                        <TableCell>套餐 / 卡号</TableCell>
                        <TableCell align="right">充值字数</TableCell>
                        <TableCell align="right">实付金额</TableCell>
                        <TableCell>充值时间</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 6, borderBottom: 0 }}>
                            <CircularProgress size={28} sx={{ color: '#00e5ff' }} />
                          </TableCell>
                        </TableRow>
                      ) : records.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 6, borderBottom: 0 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(214,227,252,0.4)' }}>
                              暂无充值记录
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        records.map((record, index) => {
                          const cfg = SOURCE_CONFIG[record.source] || { label: record.source, color: '#999', bg: 'rgba(153,153,153,0.15)' };
                          return (
                            <TableRow
                              key={record.orderNo || record.transactionId || index}
                              sx={{
                                '& td': {
                                  color: '#d6e3ff',
                                  fontSize: { xs: '0.7rem', sm: '0.78rem' },
                                  py: 0.75,
                                  px: 1,
                                  borderBottom: '1px solid rgba(0,229,255,0.06)',
                                  backgroundColor: 'transparent',
                                  whiteSpace: 'nowrap'
                                },
                                '&:hover td': { backgroundColor: 'rgba(0,229,255,0.04)' },
                                transition: 'background-color 0.15s'
                              }}
                            >
                              <TableCell sx={{ pl: { xs: 1, sm: 2 } }}>
                                <Chip
                                  label={cfg.label}
                                  size="small"
                                  sx={{
                                    fontSize: '0.65rem', fontWeight: 700, height: 20,
                                    color: cfg.color, backgroundColor: cfg.bg,
                                    border: `1px solid ${cfg.color}50`
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ maxWidth: { xs: 80, sm: 120 } }}>
                                <Typography sx={{ color: '#e4f1ff', fontWeight: 500, fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {record.user?.username || '-'}
                                </Typography>
                                <Typography sx={{ color: 'rgba(214,227,252,0.4)', fontSize: '0.62rem', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: 70, sm: 110 } }}>
                                  {record.user?.phone || record.user?.email || ''}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ maxWidth: { xs: 100, sm: 160 } }}>
                                <Tooltip title={record.packageName || record.cardKey || record.description || ''}>
                                  <Typography sx={{
                                    color: cfg.color, fontFamily: 'monospace', fontSize: '0.7rem',
                                    maxWidth: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default'
                                  }}>
                                    {record.packageName || record.cardKey || record.description || '-'}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right" sx={{ pr: { xs: 1, sm: 2 } }}>
                                <Typography sx={{ color: '#ffd600', fontWeight: 600, fontFamily: 'monospace' }}>
                                  {record.amountText || formatAmount(record.amount)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ pr: { xs: 1, sm: 2 } }}>
                                <Typography sx={{ color: record.priceText !== '-' ? cfg.color : 'rgba(214,227,252,0.4)', fontFamily: 'monospace' }}>
                                  {record.priceText || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ pr: { xs: 1, sm: 2 } }}>
                                <Typography sx={{ color: 'rgba(214,227,252,0.5)', fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                                  {formatDate(record.paidAt || record.createdAt)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* 分页 */}
                <TablePagination
                  component="div"
                  count={pagination.total}
                  page={pagination.page - 1}
                  rowsPerPage={pagination.limit}
                  onPageChange={handleChangePage}
                  labelRowsPerPage=""
                  labelDisplayedRows={({ from, to, count }) => `第 ${from}-${to} 条，共 ${count} 条`}
                  sx={{
                    color: 'rgba(214,227,252,0.65)',
                    fontSize: '0.78rem',
                    borderTop: '1px solid rgba(0,229,255,0.1)',
                    mt: 1,
                    minHeight: 48,
                    '& .MuiTablePagination-select': { display: 'none' },
                    '& .MuiIconButton-root': { color: '#00e5ff', '&:disabled': { color: 'rgba(0,229,255,0.3)' } },
                    '& .MuiTablePagination-selectLabel': { color: 'rgba(214,227,252,0.55)', fontSize: '0.78rem' }
                  }}
                />
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RechargeRecordPanel;
